import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  ContentChild,
  forwardRef,
  OnChanges,
  SimpleChanges, ElementRef
} from '@angular/core';
import { NgSelectComponent, NgOption } from '@ng-select/ng-select';
import { MultiSelectListItemTemplateDirective, MultiSelectSelectedItemTemplateDirective } from './multi-select-templates.directive';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { CompareWithFn } from '@ng-select/ng-select/lib/ng-select.component';
import { KeysPipe } from '../../pipes/keys.pipe';

/* Examples:
<ssg-multi-select [items]="items" [multiple]="true" placeholder="Search by name..." [(ngModel)]="selectedItems">
  <ng-template ssgSelectedItem let-item="item">
    <span>{{item.type}}: {{item.name}}</span>
  </ng-template>
  <ng-template ssgListItem let-item="item">
    <span>{{item.name}}</span> <span class="text-muted">{{item.type}}</span>
  </ng-template>
</ssg-multi-select>

OR (If the items can be represented by a single property)

<ssg-multi-select [items]="items" [multiple]="true" bindLabel="name" [(ngModel)]="selectedItems"></ssg-multi-select>

OR (To transform the model values to a property of the items)

<ssg-multi-select [items]="items" [multiple]="true" bindValue="id" [(ngModel)]="selectedItems"></ssg-multi-select>
*/

@Component({
  selector: 'ssg-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.less'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiSelectComponent),
    multi: true
  }],
})
export class MultiSelectComponent implements OnChanges, ControlValueAccessor {
  private _compareWith: CompareWithFn;
  private automationId: string;

  @ViewChild(NgSelectComponent, { static: true }) selectComponent: NgSelectComponent;

  @ContentChild(MultiSelectListItemTemplateDirective, { static: true, read: TemplateRef }) listItemTemplate: TemplateRef<any>;
  @ContentChild(MultiSelectSelectedItemTemplateDirective, { static: true, read: TemplateRef }) selectedItemTemplate: TemplateRef<any>;

  @Input() items: any[];
  @Input() bindValue: string;
  @Input() bindLabel: string;
  @Input() placeholder: string;
  @Input() multiple: boolean;
  @Input() appendTo: any;
  @Input() set compareWith(value: CompareWithFn) {
    this._compareWith = value;
  }
  get compareWith(): CompareWithFn {
    return this._compareWith || this._defaultCompareWith;
  }

  @Input() set disabled(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }

  get selectedItems(): NgOption[] {
    return this.selectComponent.selectedItems;
  }

  get selectedValues() {
    return this.selectComponent.selectedValues;
  }

  get hasValue() {
    return this.selectComponent.hasValue;
  }

  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('blur') blurEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('focus') focusEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('change') changeEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('open') openEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('close') closeEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('search') searchEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('clear') clearEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('add') addEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('remove') removeEvent = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-rename
  @Output('scrollToEnd') scrollToEnd = new EventEmitter<{ start: number; end: number }>();

  constructor(private elementRef: ElementRef) {
    const property = elementRef.nativeElement.dataset['automation'];
    this.automationId = property || 'multi-select';
  }

  private _defaultCompareWith(x, y): boolean {
    if (this.bindValue) {
      const properties = this.bindValue.split('.');
      const getValueOrSelf = (z) => {
        for (let i = 0; i < properties.length; i++) {
          if (i === 0 && (z === null || z === undefined || z[properties[i]] === undefined)) {
            break;
          }

          z = z[properties[i]];
        }

        return z;
      };
      x = getValueOrSelf(x);
      y = getValueOrSelf(y);
    }

    return x === y;
  }

  onBlur(data) {
    this.blurEvent.emit(data);
  }
  onFocus(data) {
    this.focusEvent.emit(data);
  }
  onChange(data) {
    this.changeEvent.emit(data);
  }
  onOpen(data) {
    this.openEvent.emit(data);
  }
  onClose(data) {
    this.closeEvent.emit(data);
  }
  onSearch(data) {
    this.searchEvent.emit(data);
  }
  onClear(data) {
    this.clearEvent.emit(data);
  }
  onAdd(data) {
    this.addEvent.emit(data);
  }
  onRemove(data) {
    this.removeEvent.emit(data);
  }
  onScrollToEnd(data) {
    this.scrollToEnd.emit(data);
  }

  open() {
    this.selectComponent.open();
  }
  close() {
    this.selectComponent.close();
  }
  focus() {
    this.selectComponent.focus();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.selectComponent.ngOnChanges(changes);
  }

  writeValue(obj: any): void {
    this.selectComponent.writeValue(obj);
  }

  registerOnChange(fn: any): void {
    this.selectComponent.registerOnChange(fn);
  }

  registerOnTouched(fn: any): void {
    this.selectComponent.registerOnTouched(fn);
  }

  setDisabledState(isDisabled: boolean): void {
    this.selectComponent.setDisabledState(isDisabled);
  }
}
